import React, { useMemo } from 'react';
import Tooltip from '../../../../../components/Tooltip';
import { toCurrency, toInt } from '../../../../../utils/numbers';
import { useAccountId } from '../../../../CreativeAnalytics/selectors';
import { Metric } from '../../../types/types';
import { formatMetricValue } from '../DetailData/utils';

export const CLIENT_SPECIFIC_COLS = {
  act_693999105130803: [
    { Header: 'Welcome Visits', accessor: 'total_welcome_visits' },
    {
      Header: 'CWPV',
      accessor: 'cost_per_welcome_visits',
      Cell: ({ value }) => toCurrency(value),
    },
  ],
};

const createColumnFromMetric = (metric: Metric) => {
  return {
    Header: metric.display_name,
    accessor: (row) => row[metric.og_metric],
    metricName: metric.og_metric,
    Cell: ({ value }) => formatMetricValue(value, metric),
  };
};

export const useSummaryCols = (
  isROAS: boolean = false,
  data,
  master_mappings: Metric[] = []
) => {
  const account_id = useAccountId();

  return useMemo(() => {
    try {
      let result = [];
      const additional = CLIENT_SPECIFIC_COLS[account_id] ?? [];

      const dynamicColumns = master_mappings
        .filter((metric) => metric.display_to_user === 'Yes')
        .map(createColumnFromMetric);

      const baseColumns = [
        ...baseSummaryCols,
        ...(isROAS ? roasSummaryCols : cpaSummaryCols),
      ].filter(
        (baseCol) =>
          !dynamicColumns.some((col) => col.metricName === baseCol.accessor)
      );

      result = [...baseColumns, ...dynamicColumns, ...additional];

      return filterCols(result, data);
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [isROAS, data, account_id, master_mappings]);
};

export const filterCols = (result, data = []) => {
  const dataHasAdNameGroup = data.some(
    (group) => !!group.ad_name_group && group.ad_name_group !== 'No Grouping'
  );
  const dataHasLabelName = data.some(
    (group) => !!group.label_name && group.label_name !== 'None'
  );

  return result.filter((col) => {
    if (col.accessor === 'ad_name_group') {
      return !!dataHasAdNameGroup;
    } else if (col.accessor === 'label_name') {
      return !!dataHasLabelName;
    } else {
      const hasValue = data.some((row) => {
        let value;

        if (col.metricName) {
          value = row[col.metricName];
        } else if (typeof col.accessor === 'function') {
          value = col.accessor(row);
        } else {
          value = row[col.accessor];
        }

        return (
          value !== null && value !== undefined && value !== 0 && value !== ''
        );
      });
      return hasValue;
    }
  });
};

export const baseSummaryCols = [
  {
    Header: 'Ad Name Group',
    accessor: 'ad_name_group',
    metricName: 'ad_name_group',
  },
  {
    width: 150,
    Header: 'Label Name',
    accessor: 'label_name',
    metricName: 'label_name',
    Cell: ({ value }) => (
      <Tooltip
        content={value}
        classmetricNameName=""
        style={{}}
        distance={10}
        disabled={false}
        placement="top"
        follow={false}
        noPadding={false}
        showOnCreate={false}
        maxWidth={200}
        interactive={true}
      >
        <div>{value}</div>
      </Tooltip>
    ),
  },
  {
    Header: 'Total Spend',
    accessor: 'total_spend',
    metricName: 'total_spend',

    Cell: ({ value }) => toCurrency(value),
  },
];

const roasSummaryCols = [
  {
    Header: 'ROAS',
    accessor: 'total_roas',
    metricName: 'total_roas',
    Cell: ({ value }) => {
      return formatROAS(value);
    },
  },
  {
    Header: 'Total Revenue',
    accessor: 'total_revenue',
    metricName: 'total_revenue',
    Cell: ({ value }) => toCurrency(value),
  },
];

const cpaSummaryCols = [
  {
    Header: 'Total Link Clicks',
    accessor: 'total_link_clicks',
    metricName: 'total_link_clicks',
  },
  {
    Header: 'Total Results',
    accessor: 'total_results',
    metricName: 'total_results',
  },

  {
    Header: 'CPA',
    accessor: 'total_cpa',
    metricName: 'total_cpa',
    Cell: ({ value }) => toCurrency(value),
  },
  {
    Header: 'Total CPC',
    accessor: 'total_cpc',
    metricName: 'total_cpc',
    Cell: ({ value }) => toCurrency(value),
  },
  {
    Header: 'CVR',
    accessor: 'CVR',
    metricName: 'CVR',
  },
];

const formatROAS = (value) => {
  if (value === 'Not Enough Data') {
    return '-';
  }

  return value?.toFixed(2) || value || '-';
};
